import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  TemplateRef,
  Type,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  AlertComponent,
  CardComponent,
  MapComponent,
  MapGeocoder,
  MapLayerDescriptor,
  MessengerService,
  RoutingService,
  __,
  blueZodiac,
  buildClusterCircleStyle,
  cantaloupe,
  cornflower,
  frenchManicure,
  hintOfMint,
  pastelGreen,
} from '@web/birdz-angular';
import { CardMapPopupComponent } from 'app/components/card-map/card-map-popup.component';
import { ModalConfirmHeavyMapLoadingComponent } from 'app/components/modal-confirm-heavy-map-loading/modal-confirm-heavy-map-loading.component';
import { MapResponse } from 'app/interfaces/map-response.interface';
import { PdsLight } from 'app/interfaces/pds-light.interface';
import { Point } from 'app/interfaces/point.interface';
import { Api } from 'app/services/api.service';
import { FilterService } from 'app/services/filter.service';
import { UserService } from 'app/services/user.service';
import Feature from 'ol/Feature';
import OlPoint from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { StyleFunction } from 'ol/style/Style';
import { Observable, distinctUntilChanged, filter, finalize, map, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-card-map',
  imports: [
    CardComponent,
    TranslocoPipe,
    AsyncPipe,
    AlertComponent,
    MapComponent,
    MatButtonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatOptionModule,
  ],
  templateUrl: './card-map.component.html',
  styleUrl: './card-map.component.scss',
})
export class CardMapComponent implements OnInit, AfterViewInit {
  private filterService = inject(FilterService);
  private api = inject(Api);
  private messenger = inject(MessengerService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private routing = inject(RoutingService);
  private ref = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(MapComponent) private mapComponent?: MapComponent;
  @ViewChild('meterOptionTpl') meterOptionTpl!: TemplateRef<{ $implicit: PdsLight }>;

  hasPerimeter$: Observable<boolean>;
  techno$: Observable<string | null>;
  isBirdzAdmin$: Observable<boolean>;
  availableTechnos = ['watermetering'];
  heavyMapThreshold = 100000; // number of pds above which the map is considered heavy
  isHeavy = false;
  isLoaded = false;
  isLoading = 0;
  hasNonGeocodedPds = false;
  hasMarkerWithUnknownState = false;
  searchVersion = 0;
  extraGeocoders: MapGeocoder[] = [];
  meterGeocoder!: MapGeocoder<PdsLight>;
  selectedPdsId?: string | null;

  readonly colors: Record<string, string> = {
    NON_EQUIPPED: frenchManicure,
    WB: hintOfMint,
    NOT_CONNECTED: cantaloupe,
    FUNCTIONAL: pastelGreen,
    NONFUNCTIONAL_HS: cornflower,
    NONFUNCTIONAL_NC: blueZodiac,
    UNKNOWN: '#aaa',
  };

  legendToggleState: Record<string, boolean> = {
    WB: true,
    NOT_CONNECTED: true,
    FUNCTIONAL: true,
    NONFUNCTIONAL_HS: true,
    NONFUNCTIONAL_NC: true,
    UNKNOWN: true,
  };

  private allPoints: Point[] = [];
  mapSource = new VectorSource<Feature>({ features: [] });
  popupComponent: Type<CardMapPopupComponent> = CardMapPopupComponent;

  private readonly pointStyle = {
    'shape-points': 24,
    'shape-radius': 5,
    'shape-stroke-width': 1,
    'shape-stroke-color': '#00000077',
    'shape-fill-color': [
      'match',
      ['get', 'state'],
      'NON_EQUIPPED',
      frenchManicure,
      'WB',
      hintOfMint,
      'NOT_CONNECTED',
      cantaloupe,
      'FUNCTIONAL',
      pastelGreen,
      'NONFUNCTIONAL_HS',
      cornflower,
      'NONFUNCTIONAL_NC',
      blueZodiac,
      '#aaa',
    ],
  };

  private readonly ungroupedPointStyle: StyleFunction = (feature) => {
    const color = this.colors[feature.get('state') ?? ''] ?? '#aaa';
    return new Style({
      image: new Circle({
        radius: 5,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#00000077', width: 1 }),
      }),
    });
  };

  private readonly spiderfyStyleCache = new Map<string, Style>();

  private getDominantColor(features: Feature[]): string {
    const colorCounts: Record<string, number> = {};
    for (const f of features) {
      const c = this.colors[f.get('state') ?? ''] ?? '#aaa';
      colorCounts[c] = (colorCounts[c] ?? 0) + 1;
    }
    const maxCount = Math.max(...Object.values(colorCounts));
    return Object.entries(colorCounts).find(([, c]) => c === maxCount)?.[0] ?? '#aaa';
  }

  private buildClusterStyle(feature: Feature): ReturnType<StyleFunction> {
    const features: Feature[] = feature.get('features') ?? [];
    if (features.length <= 1) {
      return undefined;
    }
    return buildClusterCircleStyle(this.getDominantColor(features), features.length);
  }

  private buildSpiderfyStyle(feature: Feature): Style | undefined {
    const features: Feature[] = feature.get('features') ?? [];
    if (!features.length) {
      return undefined;
    }

    const color = this.getDominantColor(features);
    if (!this.spiderfyStyleCache.has(color)) {
      this.spiderfyStyleCache.set(
        color,
        new Style({
          image: new Circle({
            radius: 5,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#00000077', width: 1 }),
          }),
        })
      );
    }
    return this.spiderfyStyleCache.get(color);
  }

  mapLayers: MapLayerDescriptor[] = [
    {
      id: 'pds',
      source: this.mapSource,
      pointStyle: this.pointStyle as any,
      clustering: { style: (f) => this.buildClusterStyle(f as Feature) },
      ungroupedPointStyle: this.ungroupedPointStyle,
      spiderfy: { style: (f) => this.buildSpiderfyStyle(f as Feature) },
    },
  ];

  constructor() {
    this.hasPerimeter$ = this.filterService.hasPerimeter$;
    this.techno$ = this.filterService.filter$.pipe(map((f) => f.techno));
    this.isBirdzAdmin$ = this.userService.user$.pipe(map(() => this.userService.isBirdzAdmin()));
  }

  ngOnInit() {
    this.clearSelectionOnFilterChange();
    this.fetchMapWhenFilterChanges();
  }

  ngAfterViewInit(): void {
    this.meterGeocoder = {
      id: 'meter',
      ctaLabel: __('GEOCODER_CTA'),
      label: __('GEOCODER_LABEL'),
      icon: 'readiness_score',
      minLength: 3,
      search: (q) => this.api.search(this.filterService.getPerimeter(), q),
      displayFn: (opt: PdsLight) => opt[opt.found_by ?? 'equipment_id'] ?? opt.equipment_id ?? opt.id,
      optionTemplate: this.meterOptionTpl,
      onSelect: (opt: PdsLight) => {
        this.selectedPdsId = opt.id;
        this.mapComponent?.selectFeatureById(opt.id);
      },
    };
    this.extraGeocoders = [this.meterGeocoder];
    this.cdr.detectChanges();
  }

  clearSelectionOnFilterChange() {
    this.filterService.filter$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.selectedPdsId = null;
          this.searchVersion++;
          this.mapComponent?.closePopup();
        })
      )
      .subscribe();
  }

  private rebuildSource(shouldFit = false) {
    const isBirdzAdmin = this.userService.isBirdzAdmin();
    const features = this.allPoints
      .filter((p) => p.state !== 'WB' || isBirdzAdmin) // exclude WB for non birdz admin
      .filter((p) => this.legendToggleState[p.state] !== false)
      .map((p) => new Feature({ geometry: new OlPoint(fromLonLat([p.lng, p.lat])), ...p }));

    this.mapSource.clear(true);
    this.mapSource.addFeatures(features);

    if (shouldFit) {
      setTimeout(() => this.mapComponent?.fit());
    }
  }

  setMarkers(points: Point[], shouldFit = false) {
    this.hasMarkerWithUnknownState = points.some((p) => p.state === 'UNKNOWN');
    this.allPoints = points;
    this.rebuildSource(shouldFit);
  }

  toggleLegendState(state: string) {
    if (this.legendToggleState[state] !== undefined) {
      this.legendToggleState[state] = !this.legendToggleState[state];
    }
    this.rebuildSource();
  }

  reset() {
    this.allPoints = [];
    this.mapSource.clear(true);
    this.hasNonGeocodedPds = false;
    this.isLoaded = false;
  }

  getFetchMapObservable(shouldFit = false): Observable<MapResponse> {
    return this.api.fetchPdsCount(this.filterService.getPerimeter()).pipe(
      tap((count) => (this.isHeavy = count > this.heavyMapThreshold)),
      switchMap(() =>
        this.isHeavy
          ? this.dialog.open(ModalConfirmHeavyMapLoadingComponent, { maxWidth: '700px' }).afterClosed()
          : of(true)
      ),
      filter((confirmed) => confirmed),
      tap(() => this.isLoading++),
      switchMap(() =>
        this.api.fetchDashboardMap(this.filterService.getPerimeter()).pipe(
          this.messenger.showFriendlyErrorMessage(),
          tap((data) => {
            this.hasNonGeocodedPds = data.non_geocoded_pds > 0;
            this.setMarkers(data.pds, shouldFit);
          }),
          finalize(() => {
            this.isLoaded = true;
            this.isLoading--;
          })
        )
      )
    );
  }

  fetchMapWhenFilterChanges() {
    this.filterService.filter$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        this.routing.pauseWhileDetached(this.ref, this.destroyRef),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        tap(() => this.reset()),
        filter(
          () =>
            this.filterService.hasPerimeter() && this.availableTechnos.includes(this.filterService.getTechno() ?? '')
        ),
        switchMap(() => this.getFetchMapObservable(true))
      )
      .subscribe();
  }

  forceFetchMap() {
    this.getFetchMapObservable().subscribe();
  }
}
